import md5 from 'js-md5'
import { componentTypes } from './FrameConfiguration.js'
import type { ComponentType, FrameConfiguration } from './FrameConfiguration.js'

function sortBy<T>(array: Array<T>, callbackfn: (value: T) => any): Array<T> {
	return array.slice(0).sort((a, b) => {
		const valA = callbackfn(a)
		const valB = callbackfn(b)
		return valA < valB ? -1 : valA > valB ? 1 : 0
	})
}

export function GetConfigurationString(configuration: FrameConfiguration): string {
	// debugger
	const config = configuration ?
		new URLSearchParams(Object.fromEntries([
			...Object.keys(configuration).filter(x => x != 'components').filter(x => x != 'engraving').map(x => [x, configuration[x]?.toString()]),
			...sortBy(Object.keys(configuration.components), x => x == 'MODEL' ? 0 : x).filter(x => ['design', 'size', 'color'].find(y => configuration.components[x][y])).map(x => [
				x,
				['design', 'size', 'color'].map(y => configuration.components?.[x]?.[y]?.replace(' ', '_').replace('+', '_') || '').join(' ')
			])
		])).toString() :
		''
	return config.replaceAll('%E2%96%A1', '□').replaceAll('%23', '□').replaceAll('%2F', '/')
}

export function GetConfigurationFromString(value: string, allowEngraving: boolean = false): FrameConfiguration {
	const parameters = new URLSearchParams(value)
	if (!parameters.get('MODEL') && !parameters.get('productline')) return
	Array.from(parameters.keys()).filter(x => !(x == 'productline' || x == 'isRimless' || (allowEngraving && x == 'engraving') || componentTypes.includes(x as ComponentType))).forEach(x => parameters.delete(x))
	const properties = Array.from(parameters).filter(x => x[0] != x[0].toUpperCase())
	const components = Array.from(parameters).filter(x => x[0] == x[0].toUpperCase()).map(x => [x[0], Object.fromEntries(x[1].split(' ').map((v, i) => [['design', 'size', 'color'][i], v]))])

	const configuration = Object.fromEntries([...properties, ['components', Object.fromEntries(components)]]) as FrameConfiguration
	configuration.isRimless = parameters.get('isRimless') == 'true'

	return configuration
}

export function getConfigurationFilename(configuration: FrameConfiguration) {
	return `${configuration.productline}/${configuration.components.MODEL.design}/${configuration.components.TEMPLE.design}/${md5(GetConfigurationString(configuration))}`
}

export function GetFileName(config: FrameConfiguration, part: 'Bridge' | 'Nosepads' | 'Lenses' | 'Clip' | 'Bushing' | 'Temple' | 'Front'): string | null {
	const c = config.components

	const isPrecious = (color?: string) => /^(AU750|PT950)/.test(color ?? '')
	const join = (design?: string, size?: string) => [design, size].filter(Boolean).join(' ')
	const isKidTeenTemple = config.productline === 'RIMKID' || config.productline === '1000TEEN'
	const isKidTeenFront = config.productline === 'RIMKID' || config.productline === 'RIMTEEN' || config.productline === '1000TEEN'

	if (part === 'Bridge') {
		if (!c.BRIDGE) return null
		return `Bridges/${isPrecious(c.BRIDGE.color) ? 'Precious' : 'Titanium'}/${join(c.BRIDGE.design, c.BRIDGE.size)}`
	}

	if (part === 'Nosepads') {
		if (!c.NOSEPAD?.design) return null
		return `Nosepads/${isPrecious(c.NOSEPAD?.color) ? 'Precious' : 'Titanium'}/${c.NOSEPAD.design}`
	}

	if (part === 'Clip') {
		if (!c.CLIP) return null
		return `Clips/${isPrecious(c.CLIP?.color) ? 'Precious' : 'Titanium'}/${join(c.CLIP.design, c.CLIP.size)}`
	}

	if (part === 'Temple') {
		if (!c.TEMPLE) return null
		const precious = isPrecious(c.TEMPLE?.color)
		const modelType = precious ? 'Precious' : isKidTeenTemple ? 'Kids' : 'Titanium'
		return `Temples/${modelType}/${join(c.TEMPLE.design, c.TEMPLE.size)}`
	}

	if (part === 'Bushing') {
		if (!c.BUSHING?.design) return null
		return `Bushings/${isPrecious(c.TEMPLE?.color) ? 'Precious' : 'Titanium'}/${c.BUSHING.design}`
	}

	if (part === 'Lenses') {
		if (c.MODEL.design == 'GARNITURE') return null
		const lensCurve = parseInt(c.LENSES?.design?.match(/\d+/)?.[0] ?? '4')
		return `${c.GROOVE?.color ? 'Lenses/Groove' : 'Lenses'}/${c.MODEL.design}${(lensCurve === 2 ? '-B2' : '')} ${c.MODEL.size || ''}`.trim()
	}

	if (part === 'Front') {
		if (!c.MODEL) return null
		const precious = ['FRONT', 'UPPERRIM', 'BRIDGE', 'CLIPS', 'TEMPLE'].some(x => isPrecious(c[x]?.color))
		const modelType = precious ? 'Precious' : isKidTeenFront ? 'Kids' : 'Titanium'
		const lensCurve = parseInt(c.LENSES?.design?.match(/\d+/)?.[0] ?? '4')
		const modelDesign = c.MODEL.design + (lensCurve === 2 ? '-B2' : '')
		return `Fronts/${modelType}/${join(modelDesign, c.MODEL.size)}`
	}

	return null
}

export function GetRequiredFilenames(frame: FrameConfiguration): string[] {
	const glbs = (frame.isRimless ?
		[GetFileName(frame, 'Bridge'), GetFileName(frame, 'Lenses'), GetFileName(frame, 'Nosepads'), GetFileName(frame, 'Clip'), GetFileName(frame, 'Temple'), GetFileName(frame, 'Bushing')] :
		[GetFileName(frame, 'Front'), GetFileName(frame, 'Nosepads'), GetFileName(frame, 'Bushing'), GetFileName(frame, 'Temple')]).filter(Boolean)

	const materials = Object.values(frame.components).map(x => x.color).filter(Boolean)

	return [...new Set(glbs.map(x => x + '.glb').concat(materials.map(x => 'Materials/' + x + '.json')))]
}
